import { Calculation } from "@/models/Calculation";
import { Federation } from "@/models/Federation";
import mergeInvoiceDataAndCalculations from "@/utils/calculations-invoice-merger";
import { missingInvoiceData } from "@/utils/missing-calculations";
import OrderLineType from "./order-line-type";

type InvoiceData = {
  nhoNumber: number;
  orderLineType: OrderLineType;
  productNumber: string;
  project: number;
  department: number;
  lastChanged?: string;
};

type InvoiceDataDisplay = InvoiceData & {
  federationName?: string;
};

const prepareForServer = (item: InvoiceDataDisplay): InvoiceData => {
  const { lastChanged, federationName, ...newItem } = item;
  return newItem;
};

const checkEquality = (x: InvoiceData, y: InvoiceData): boolean => {
  const xn1 = x.nhoNumber;
  const xn2 = y.nhoNumber;
  if (xn1 === xn2 && x.orderLineType === y.orderLineType) {
    return true;
  }
  return false;
};

const checkExistance = (items: InvoiceData[], x: InvoiceData): boolean =>
  items.some((item) => checkEquality(item, x));

const defaultItem: InvoiceData = {
  nhoNumber: 0,
  orderLineType: OrderLineType.Contingent,
  productNumber: "1000",
  project: 0,
  department: 0,
  lastChanged: "",
};

const createUniqueItem = (
  nhoNumber: number,
  orderLineType: OrderLineType,
  productNumber: string
): InvoiceData => ({
  nhoNumber,
  orderLineType,
  productNumber: productNumber,
  project: defaultItem.project,
  department: defaultItem.department,
  lastChanged: defaultItem.lastChanged,
});

const getOrderLineTypeFromCalculationType = (
  calculationType: string
): [OrderLineType, OrderLineType] => {
  switch (calculationType) {
    case "Contingent":
      return [OrderLineType.Contingent, OrderLineType.ContingentDiscount];
    case "ServiceFee":
      return [OrderLineType.ServiceFee, OrderLineType.ServiceFeeDiscount];
    default:
      return [OrderLineType.Contingent, OrderLineType.ContingentDiscount];
  }
};

const filterInvoiceData = (
  invoiceData: InvoiceData[],
  calculations: Calculation[],
  federations: Federation[] | undefined,
  filter: {
    name: string;
    orderLineTypes: string[];
    showOnlyMissingRows: boolean;
  }
): InvoiceData[] => {
  const { name, orderLineTypes, showOnlyMissingRows } = filter;

  let filteredInvoiceData = mergeInvoiceDataAndCalculations(
    invoiceData,
    calculations
  );

  // FILTER showOnlyMissingRows - Only show missing rows
  if (showOnlyMissingRows) {
    filteredInvoiceData = missingInvoiceData(invoiceData, federations ?? []);
  }

  //FILTER name/number
  if (name !== "") {
    filteredInvoiceData = filteredInvoiceData.filter((invoice: InvoiceData) => {
      const fOrg = invoice.nhoNumber.toString();
      const federationName =
        federations?.find((f: Federation) => f.nhoNumber === invoice.nhoNumber)
          ?.name ?? invoice.nhoNumber.toString();
      const fName = federationName.toLowerCase().trim();
      return fOrg.includes(name) || fName.includes(name);
    });
  }

  //FILTER orderLine
  if (orderLineTypes.length > 0) {
    filteredInvoiceData = filteredInvoiceData.filter((invoice: InvoiceData) => {
      return orderLineTypes.includes(invoice.orderLineType.toString());
    });
  }

  return filteredInvoiceData;
};

export const InvoiceDataUtils = {
  prepareForServer,
  defaultItem,
  checkEquality,
  checkExistance,
  createUniqueItem,
  getOrderLineTypeFromCalculationType,
  filterInvoiceData,
};

export default InvoiceData;
